import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {AgGridReact} from '@ag-grid-community/react'
import {ColDef, ModuleRegistry} from '@ag-grid-community/core'
import {ExcelExportModule} from '@ag-grid-enterprise/excel-export'
import {RowGroupingModule} from '@ag-grid-enterprise/row-grouping'
import {SetFilterModule} from '@ag-grid-enterprise/set-filter'
import {StatusBarModule} from '@ag-grid-enterprise/status-bar'
import {LicenseManager} from '@ag-grid-enterprise/core'
import {Loader} from '@consta/uikit/LoaderCanary'
import moment from 'moment'

import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-alpine.css'
import '../../../component/css/agGridGraph.css'
import {API_CLIFF} from '../../../service/api.endpoint'
import api from '../api/api'
import ModalCliffView from './modal.cliff.cliffView'


ModuleRegistry.registerModules([
    ExcelExportModule,
    RowGroupingModule,
    SetFilterModule,
    StatusBarModule,
])

const license = process.env.REACT_APP_AGGRID
if (license) {
    LicenseManager.setLicenseKey(license)
}

type CliffViewMode = 'active' | 'all'

interface CliffListProps {
    isUpdate: number
    isExport: number
    viewMode: CliffViewMode
    onSummaryChange: (summary: {total: number, active: number}) => void
}

const formatDate = (value: string | null | undefined) => (
    value ? moment(value).format('DD.MM.YYYY') : '—'
)

const GridCliffList: React.FC<CliffListProps> = ({
    isUpdate,
    isExport,
    viewMode,
    onSummaryChange,
}) => {
    const gridRef = useRef<any>()
    const [rowData, setRowData] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [graphParam, setGraphParam] = useState<any>()
    const [graphNode, setGraphNode] = useState<any>()

    const columnDefs = useMemo<ColDef<any>[]>(() => [
        {
            field: 'current_org_name',
            headerName: 'Текущий подрядчик',
            rowGroup: true,
            hide: true,
            enableRowGroup: true,
        },
        {
            field: 'wells.name',
            headerName: 'Скважина',
            pinned: 'left',
            width: 105,
            minWidth: 90,
            filter: 'agTextColumnFilter',
        },
        {
            field: 'deposit_name',
            headerName: 'Месторождение',
            width: 145,
            minWidth: 120,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'dns_name',
            headerName: 'ДНС',
            width: 95,
            minWidth: 80,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'wells.bush.name',
            headerName: 'Куст',
            width: 90,
            minWidth: 75,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'status_display',
            headerName: 'Статус',
            width: 105,
            minWidth: 95,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
            cellStyle: (params: any): any => params.data?.status === 'ACTIVE'
                ? {background: '#fff4e5', color: '#8a4b08', fontWeight: 600}
                : {background: '#eaf5e8', color: '#2f6b2f', fontWeight: 600},
        },
        {
            field: 'cliff_date',
            headerName: 'Дата обрыва',
            width: 115,
            minWidth: 105,
            sort: 'desc',
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'days_open',
            headerName: 'Дней',
            width: 75,
            minWidth: 68,
            type: 'numericColumn',
            cellStyle: (params: any): any => (
                params.data?.status === 'ACTIVE' && Number(params.value) >= 30
                    ? {color: '#b42318', fontWeight: 700}
                    : null
            ),
        },
        {
            field: 'depth',
            headerName: 'Глубина, м',
            width: 100,
            minWidth: 90,
            type: 'numericColumn',
        },
        {
            field: 'extraction_date',
            headerName: 'Дата извлечения',
            width: 130,
            minWidth: 118,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'org_cliff.name',
            headerName: 'Орг. обрыва',
            width: 175,
            minWidth: 135,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'org_extract.name',
            headerName: 'Орг. извлечения',
            width: 175,
            minWidth: 135,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
            valueFormatter: (params: any) => params.value || '—',
        },
        {
            field: 'comment',
            headerName: 'Примечание',
            minWidth: 220,
            flex: 1,
            valueFormatter: (params: any) => params.value || '—',
        },
        {
            field: 'created_at',
            headerName: 'Добавлен',
            hide: true,
            valueFormatter: (params: any) => (
                params.value ? moment(params.value).add(5, 'hour').format('DD.MM.YYYY HH:mm') : '—'
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Обновлён',
            hide: true,
            valueFormatter: (params: any) => (
                params.value ? moment(params.value).add(5, 'hour').format('DD.MM.YYYY HH:mm') : '—'
            ),
        },
    ], [])

    const defaultColDef = useMemo<ColDef<any>>(() => ({
        sortable: true,
        resizable: true,
        suppressHeaderContextMenu: true,
    }), [])

    const autoGroupColumnDef = useMemo<ColDef<any>>(() => ({
        headerName: 'Подрядчик',
        pinned: 'left',
        minWidth: 235,
        width: 260,
        cellRendererParams: {
            suppressCount: false,
        },
    }), [])

    const statusBar = useMemo(() => ({
        statusPanels: [
            {statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left'},
        ],
    }), [])

    const fetchData = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await api.get(API_CLIFF, {
                params: viewMode === 'active' ? {active: 1} : {},
            })
            const rows = Array.isArray(response.data) ? response.data : []
            setRowData(rows)
            onSummaryChange({
                total: rows.length,
                active: rows.filter((item: any) => item.status === 'ACTIVE').length,
            })
        } catch (error) {
            console.error('Не удалось загрузить обрывы:', error)
            setRowData([])
            onSummaryChange({total: 0, active: 0})
        } finally {
            setIsLoading(false)
        }
    }, [onSummaryChange, viewMode])

    useEffect(() => {
        fetchData()
    }, [fetchData, isUpdate])

    useEffect(() => {
        if (isExport && gridRef.current?.api) {
            gridRef.current.api.exportDataAsExcel({
                fileName: `obryvy_${moment().format('YYYY-MM-DD')}.xlsx`,
            })
        }
    }, [isExport])

    const openRecord = (params: any) => {
        if (!params.data) return
        setGraphParam({collName: params.colDef.field})
        setGraphNode(params.node)
        setIsModalOpen(true)
    }

    const handleModalClose = async () => {
        setIsModalOpen(false)
        await fetchData()
    }

    return (
        <div className="cliff-grid-wrapper">
            <div className="ag-theme-alpine cliff-grid">
                {isLoading ? (
                    <div className="cliff-grid-loader">
                        <Loader/>
                    </div>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        autoGroupColumnDef={autoGroupColumnDef}
                        groupDisplayType="singleColumn"
                        groupDefaultExpanded={0}
                        rowGroupPanelShow="always"
                        animateRows
                        getRowId={(params: any) => params.data.pk}
                        statusBar={statusBar}
                        onCellDoubleClicked={openRecord}
                    />
                )}
            </div>
            <ModalCliffView
                isOpen={isModalOpen}
                onClose={handleModalClose}
                props={graphParam}
                nodeData={graphNode}
            />
        </div>
    )
}

export default GridCliffList
